import { useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  supportedLanguages,
  defaultLanguage,
  type SupportedLanguage,
} from '@/i18n'
import { localizedRoutes } from '@/i18n/routes'
import { Layout } from '@/components/Layout/Layout'

const HomePage = lazy(() => import('@/pages/HomePage'))
const AziendaPage = lazy(() => import('@/pages/AziendaPage'))
const PersonePage = lazy(() => import('@/pages/PersonePage'))
const ServiziPage = lazy(() => import('@/pages/ServiziPage'))
const ContattiPage = lazy(() => import('@/pages/ContattiPage'))
const PrivacyPage = lazy(() => import('@/pages/PrivacyPage'))

function LanguageWrapper() {
  const { lang } = useParams<{ lang: string }>()
  const { i18n } = useTranslation()

  useEffect(() => {
    if (lang && supportedLanguages.includes(lang as SupportedLanguage)) {
      if (i18n.language !== lang) {
        // Precarica le risorse prima del cambio: evita che l'albero sospenda
        // a metà navigazione (vedi LanguageSelector.switchLanguage).
        i18n.loadLanguages(lang).then(() => i18n.changeLanguage(lang))
      }
    }
  }, [lang, i18n])

  // Redirect invalid language to default
  if (!lang || !supportedLanguages.includes(lang as SupportedLanguage)) {
    return <Navigate to={`/${defaultLanguage}`} replace />
  }

  const l = lang as SupportedLanguage

  return (
    <Layout>
      <Suspense fallback={<div className="flex min-h-[60vh] items-center justify-center"><div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-500 border-t-transparent" /></div>}>
        <Routes>
          <Route index element={<HomePage />} />
          <Route path={localizedRoutes.azienda[l]} element={<AziendaPage />} />
          <Route path={localizedRoutes.persone[l]} element={<PersonePage />} />
          <Route path={localizedRoutes.servizi[l]} element={<ServiziPage />} />
          <Route path={localizedRoutes.contacts[l]} element={<ContattiPage />} />
          <Route path={localizedRoutes.privacy[l]} element={<PrivacyPage />} />
          <Route path="*" element={<Navigate to={`/${l}`} replace />} />
        </Routes>
      </Suspense>
    </Layout>
  )
}

export function LanguageRouter() {
  return (
    <Routes>
      {/* Redirect root to default language */}
      <Route path="/" element={<Navigate to={`/${defaultLanguage}`} replace />} />

      {/* Language-prefixed routes */}
      <Route path="/:lang/*" element={<LanguageWrapper />} />
    </Routes>
  )
}
